import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, Shield, Lock, Eye, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-green-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center text-blue-600 hover:text-blue-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to IndexMaster
            </Button>
            <div className="flex items-center space-x-2">
              <Shield className="w-6 h-6 text-green-600" />
              <span className="font-semibold text-slate-800">Privacy Policy</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-blue-100">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold text-slate-800 mb-4">
              Privacy Policy
            </CardTitle>
            <p className="text-slate-600">
              This privacy statement is applicable to <strong>indexmaster.in</strong>
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </CardHeader>

          <CardContent className="prose prose-slate max-w-none">
            {/* Introduction */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <Lock className="w-5 h-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-slate-800">Our Commitment to Privacy</h3>
              </div>
              <p className="text-slate-700 leading-relaxed">
                Composite Investments Pvt Ltd (CIPL) does not collect personal information about individuals except when such individuals specifically provide such information on a voluntary basis. The privacy and protection of your data and information provided to us is of vital importance. At CIPL we are strongly committed to protecting the personal and financial information that you submit to us.
              </p>
            </div>

            {/* Information Use */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <Eye className="w-5 h-5 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold text-slate-800">How We Use Your Information</h3>
              </div>
              <p className="text-slate-700 leading-relaxed mb-4">
                Personal information of individual users will not be sold or otherwise transferred to unaffiliated third parties without your approval at the time of collection. CIPL shall ensure to safeguard the security and confidentiality of any information you share with us. Any of your personally identifiable information obtained by us shall not be used or shared other than for the purposes to which you consent.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                However despite our utmost efforts to protect your personal information, CIPL cannot warrant the security of any information you transmit to us through our online services. By accepting this privacy policy you accept that such transmission of your personal information is done at your own risk.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Your inputs / queries as a registered user are required to serve you better and the same shall not be shared with anyone without your consent. However we may disclose your personal data to agents or contractors of CIPL and / or its group companies / affiliates to enable processing of transactions or communications with you on need basis. However it shall be on the basis that the agents are required to keep the information confidential and will not use the information for any other purpose other than to carry out the services they are performing for CIPL and/or its group companies / affiliates.
              </p>
            </div>

            {/* Exceptions */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
                <h3 className="text-lg font-semibold text-slate-800">Exceptions</h3>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-slate-700 leading-relaxed">
                  We will not voluntarily disclose your information with us but in exceptional circumstances we may do so if we are required by a Court Order, by Government Authorities, by Law Enforcement Authorities or by other legal processes or where it becomes necessary to do so to protect the rights or property of CIPL and / or its group companies / affiliates etc.
                </p>
              </div>
            </div>

            {/* Cookies */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <div className="w-5 h-5 bg-blue-600 rounded-full mr-2 flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
                <h3 className="text-lg font-semibold text-slate-800">Cookies</h3>
              </div>
              <p className="text-slate-700 leading-relaxed">
                You are also being made aware that non-personal information and data may be automatically collected through the standard operation of indexmaster.in internet servers or through the use of "cookies" and that indexmaster.in cannot control the use of cookies or the resulting information by advertisers or third parties hosting data for Indexmaster.in.
              </p>
            </div>

            {/* Password Policy */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <Lock className="w-5 h-5 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold text-slate-800">Password Policy</h3>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-slate-700 leading-relaxed">
                  Do help us to protect your privacy by maintaining the secrecy of the username and password you use for any of our services.
                </p>
              </div>
            </div>

            {/* Policy Changes */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <Shield className="w-5 h-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-slate-800">Changes to this Privacy Policy</h3>
              </div>
              <p className="text-slate-700 leading-relaxed">
                CIPL reserves the right to change or update this Privacy Policy or any other of our Policies/Practices at any time. The same shall be notified to you by posting such changes or updated Privacy Policy on the page. Any changes or updates will be effective immediately upon posting to this web site.
              </p>
            </div>

            {/* Disclaimer */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-5 h-5 text-orange-600 mr-2" />
                <h3 className="text-lg font-semibold text-slate-800">No Contractual Obligation</h3>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-slate-700 leading-relaxed">
                  Please note that this privacy policy does not create any contractual or other legal rights in or on behalf of any party, nor is it intended to do so.
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-slate-50 rounded-lg p-6 text-center">
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Questions About This Policy?</h3>
              <p className="text-slate-600 mb-4">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="text-sm text-slate-600">
                <p><strong>Composite Investments Private Limited</strong></p>
                <p>Email: anuraag@compositeinvestments.com</p>
                <p>Phone: +91 98802 72472</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};